<template>
  <div :key="$store.getters['resource/currentResource'].key">

    <loader v-if="loading" :styleObject="{height: '160px!important'}"/>

    <b-form
      v-else
      @submit.prevent="debounceSubmit"
      novalidate>

      <!-- Main fields -->
      <b-row class="align-items-center">

        <!-- Event -->
        <b-col md="4">
          <div class="position-relative mb-4"
               id="event_id">
            <label class="d-block">{{$t('form.event')}}
              <span class="text-danger">{{$t('form.required_star')}}</span>
            </label>
            <multiselect
              v-model="params.event_id"
              :loading="isSelectLoading.events"
              deselectLabel="X"
              selectLabel=""
              selectedLabel=""
              :multiple="false"
              label="title"
              track-by="title"
              :options="events"
              @search-change="query => {debounceGetSelectOptions('events?limit=100', 'events', 'title', query)}">
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>
            </multiselect>

            <div class="invalid-feedback position-absolute mt-0 text-left"
                 :class="[formValidated && !params.event_id ? 'd-block' : '']"
                 style="bottom: -16px">
              {{$t('errors.error_validation')}}
            </div>

          </div>
        </b-col>

        <!-- Type -->
        <b-col md="4">
          <div class="position-relative mb-4"
               id="type">
            <label class="d-block">{{$t('form.type')}}
              <span class="text-danger">{{$t('form.required_star')}}</span>
            </label>
            <multiselect
              :required="true"
              v-model="type"
              deselectLabel="X"
              selectLabel=""
              selectedLabel=""
              :multiple="false"
              label="label"
              track-by="label"
              :options="types"
              :searchable="false"
            >
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>
            </multiselect>

            <div class="invalid-feedback position-absolute mt-0 text-left"
                 :class="[formValidated && !type ? 'd-block' : '']"
                 style="bottom: -16px">
              {{$t('errors.error_validation')}}
            </div>

          </div>
        </b-col>

        <!-- Condition -->
        <b-col v-if="showCondition" md="2">
          <div class="position-relative mb-4">
            <label class="d-block">{{$t('form.condition')}}</label>
            <multiselect
              id="condition"
              v-model="condition"
              deselectLabel=""
              selectLabel=""
              selectedLabel=""
              :multiple="false"
              label="label"
              track-by="label"
              :options="conditions"
              :searchable="false"
              :allow-empty="false"
              :preselectFirst="true">
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>
            </multiselect>

          </div>
        </b-col>

        <b-col md="2">
          <b-form-checkbox v-model="params.active">
            {{$t('form.active')}}
          </b-form-checkbox>
        </b-col>

        <!-- Event Result -->
        <b-col md="4" v-if="showCondition && condition.value === 'boolean'">
          <div v-if="!loading" class="position-relative mb-4">
            <label class="d-block">{{$t('form.event_result')}}</label>
            <multiselect
              id="eventResult"
              v-model="params.value_from_event"
              deselectLabel=""
              selectLabel=""
              selectedLabel=""
              :preselectFirst="true"
              :allow-empty="false"
              :multiple="false"
              label="label"
              track-by="label"
              :options="eventResults"
              :searchable="false">
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>
            </multiselect>

          </div>
        </b-col>

        <!-- Match String -->
        <b-col md="4" v-if="showCondition && (condition.value === 'match' || condition.value === 'equals')">
          <b-input-group v-if="!loading" class=" flex-column position-relative">
            <label class="d-block">{{ condition.value === 'match' ? $t('form.match_string') : $t('form.equals_string') }}
              <span class="text-danger">{{$t('form.required_star')}}</span>
            </label>
            <b-form-input
              :required="true"
              v-model.trim="params.value_from_event"
              class="mb-4 rounded"
            >
            </b-form-input>

            <div class="invalid-feedback position-absolute mt-0 text-left"
                 :class="[formValidated && showCondition && !params.value_from_event ? 'd-block' : '']"
                 style="bottom: 0">
              {{$t('errors.error_validation')}}
            </div>

          </b-input-group>
        </b-col>

        <!-- Webhook URL -->
        <b-col md="4" v-if="showWebhook">
          <b-input-group v-if="!loading" class=" flex-column position-relative">
            <label class="d-block">{{$t('form.webhook_url')}}
            </label>
            <b-form-input
                    :required="false"
                    v-model.trim="settings.webhook_url"
                    class="mb-4 rounded"
            >
            </b-form-input>

            <div class="invalid-feedback position-absolute mt-0 text-left"
                 :class="[formValidated && showWebhook  && !isWebhookUrlValid ? 'd-block' : '']"
                 style="bottom: 0">
              {{$t('errors.error_validation')}}
            </div>

          </b-input-group>
        </b-col>

        <!-- Mail Template -->
        <b-col v-if="showMailTemplate" md="4">
          <div class="position-relative mb-4"
               id="template_id">
            <label class="d-block">{{$t('form.mail_template')}}
              <span class="text-danger">{{$t('form.required_star')}}</span>
            </label>
            <multiselect
              v-model="params.mail_template_id"
              :loading="isSelectLoading.mailTemplates"
              deselectLabel="X"
              selectLabel=""
              selectedLabel=""
              :multiple="false"
              label="name"
              track-by="name"
              :options="mailTemplates"
              :placeholder="$t('transactions.type_to_search')"
              @search-change="query => {debounceGetSelectOptions('mail-templates?limit=100', 'mailTemplates', 'name', query)}">
              <template slot="noResult">
                {{$t('global.no_results')}}
              </template>
            </multiselect>

            <div class="invalid-feedback position-absolute mt-0 text-left"
                 :class="[formValidated && !params.mail_template_id ? 'd-block' : '']"
                 style="bottom: -16px">
              {{$t('errors.error_validation')}}
            </div>

          </div>
        </b-col>

        <b-col v-if="showMailTemplate && params.mail_template_id" md="4">
          <b-button
            class="px-5 py-2 mt-1"
            variant="outline-primary"
            :to="`/emails/edit/${
              params.mail_template_id.value
                ? params.mail_template_id.value
                : params.mail_template_id.id
            }`"
            target="_blank"
          >
            {{ $t('email_templates.edit_template') }}
          </b-button>
        </b-col>

        <b-col md="12" v-if="!!paramsOptions.wallkit.length || !!paramsOptions.external.length">
          <b-row v-for="(param, index) in additionalParams" :key="index">

            <b-col>
              <div class="position-relative mb-4">
                <label class="d-block">{{$t('event.wallkit_field')}}</label>
                <multiselect
                  v-model="additionalParams[index].wallkit"
                  deselectLabel=""
                  selectLabel=""
                  selectedLabel=""
                  :multiple="false"
                  :options="paramsOptions.wallkit"
                  :searchable="true"
                  :allow-empty="false"
                  :preselectFirst="true">
                  <template slot="noResult">
                    {{$t('global.no_results')}}
                  </template>
                </multiselect>
              </div>
            </b-col>

            <b-col>
              <b-input-group v-if="showAdditionalParamsAsInputText" class=" flex-column position-relative">
                <label class="d-block">{{type.label}} {{$t('event.field')}}
                  <span class="text-danger">{{$t('form.required_star')}}</span>
                </label>
                <b-form-input
                    :required="true"
                    v-model.trim="additionalParams[index].external.value || additionalParams[index].external"
                    class="mb-4 rounded"
                >
                </b-form-input>
                <div class="invalid-feedback position-absolute mt-0 text-left"
                     :class="[formValidated && showCondition && !additionalParams[index].external ? 'd-block' : '']"
                     style="bottom: 0">
                  {{$t('errors.error_validation')}}
                </div>
              </b-input-group>
              <div class="position-relative mb-4" v-else>
                <label class="d-block">{{type.label}} {{$t('event.field')}}</label>
                <multiselect
                  v-model="additionalParams[index].external"
                  deselectLabel=""
                  selectLabel=""
                  selectedLabel=""
                  :multiple="false"
                  :options="paramsOptions.external"
                  label="label"
                  track-by="label"
                  :searchable="true"
                  :allow-empty="false"
                  :preselectFirst="true">
                  <template slot="noResult">
                    {{$t('global.no_results')}}
                  </template>
                </multiselect>
              </div>
            </b-col>

            <b-col md="1" class="d-flex align-items-center">
              <b-button
                @click="deleteItemByIndex(additionalParams, index)"
                variant="outline-primary"
                size="sm"
                style="height: 34px; width: 34px"
              >
                <i class="fas fa-trash fa-lg"></i>
              </b-button>
            </b-col>

          </b-row>
          <b-button
            @click="addReactiveItemToArray(additionalParams, {wallkit: '', external: ''})"
            variant="outline-primary"
            size="sm"
            style="height: 34px; width: 34px"
          >
            <i class="fas fa-plus fa-lg"></i>
          </b-button>
        </b-col>

      </b-row>

      <form-buttons></form-buttons>

    </b-form>

  </div>
</template>
<script>
  import axios from '@/utils/axios';
  import { validateUrl } from '../../utils/validate-url';

  export default {
    middleware: 'auth',
    props: {
      prefillParams: {
        type: Object
      },
    },
    data: () => {
      return {
        formValidated: false,
        isSelectLoading: {},
        loading: true,
        type: '',
        condition: '',
        settings: {
          webhook_url: null
        },
        params: {
          active: true,
          event_id: null,
          mail_template_id: null,
          value_from_event: null,
          params: []
        },
        events: [],
        showAddMore: false,
        additionalParams: [
          {
            wallkit: '',
            external: ''
          }
        ],
        paramsOptions: {
          wallkit: [],
          external: []
        },
        mailTemplates: [],
        debounceSubmit: () => {
        },
        debounceGetSelectOptions: () => {
        }
      }
    },
    created() {
      if (this.prefillParams) {
        this.createPrefillParams()
      }
      this.getSelectOptions('events?limit=100', 'events');
      this.getSelectOptions('mail-templates?limit=100', 'mailTemplates');
      this.debounceSubmit = _.debounce(this.submit, 1000);
      this.debounceGetSelectOptions = _.debounce(this.getSelectOptions, 500);
    },
    computed: {
      types: () => {
        return [
          {label: 'Admin Mail', value: 'admin_mail'},
          // {label: 'Campaignmonitor', value: 'campaignmonitor'},
          {label: 'Hubspot', value: 'hubspot'},
          {label: 'Mail', value: 'mail'},
          {label: 'Mailchimp', value: 'mailchimp'},
          {label: 'Klaviyo', value: 'klaviyo'},
          {label: 'ActiveCampaign', value: 'activecampaign'},
          {label: 'Moderator Mail', value: 'moderator_mail'},
          {label: 'Segment', value: 'segment'},
          {label: 'Slack', value: 'slack'},
          {label: 'Streak', value: 'streak'},
          {label: 'Subscription', value: 'subscription'},
          {label: 'Webhook', value: 'webhook'},
        ]
      },
      conditions: () => {
        return [
          {label: 'Any', value: 'any'},
          {label: 'Boolean', value: 'boolean'},
          {label: 'Match', value: 'match'},
          {label: 'Equals', value: 'equals'},
        ]
      },
      eventResults: () => {
        return [
          {label: 'True', value: true},
          {label: 'False', value: false},
        ]
      },
      showCondition() {
        return this.type.value !== 'hubspot'
            && this.type.value !== 'mailchimp'
            && this.type.value !== 'streak'
            && this.type.value !== 'segment'
      },
      showMailTemplate() {
        return this.type.value === 'mail'
            || this.type.value === 'admin_mail'
            || this.type.value === 'moderator_mail'
      },
      showWebhook() {
        return this.type.value === 'webhook'
      },
      formIsFilled() {
        if (this.condition.value === 'match') {
          if (this.showMailTemplate) {
            return this.type && this.params.event_id && this.params.value_from_event && this.params.mail_template_id
          } else {
            return this.type && this.params.event_id && this.params.value_from_event
          }
        } else {
          return this.type && this.params.event_id
        }
      },
      showAdditionalParamsAsInputText() {
        return this.type.value === 'segment'
      },
      isWebhookUrlValid() {
        return validateUrl(this.settings.webhook_url);
      }
    },
    methods: {
      createPrefillParams() {
        this.params.active = this.prefillParams.active;
        this.type = this.formatOption(this.prefillParams.type, 'label');
        this.condition = this.formatOption(this.prefillParams.condition, 'label');

        if (this.condition.value === 'boolean') {
          this.params.value_from_event = this.formatOption(this.prefillParams.value_from_event, 'label');
        } else {
          this.params.value_from_event = this.prefillParams.value_from_event
        }

        if (this.prefillParams.template) {
          this.params.mail_template_id = {
            name: this.prefillParams.template.name,
            value: this.prefillParams.mail_template_id
          };
        }
        if (this.prefillParams.event) {
          this.params.event_id = {
            title: this.prefillParams.event.title,
            value: this.prefillParams.event_id
          };
        }
        if (!!this.prefillParams.params.length) {
          this.additionalParams = this.prefillParams.params
        }

        if (this.prefillParams.settings) {
          this.settings = this.prefillParams.settings;
        }
        this.$forceUpdate();

      },
      getSelectOptions(url, name, filter_field = null, value = null) {
        this.$set(this.isSelectLoading, name, true);
        const params = {filter: {}};
        if (filter_field && value) {
          params.filter[filter_field] = value
        }
        try {
          this.getData(url, {params}).then(({data}) => {
            this[name] = data.items
          }).then(() => {
            this.$set(this.isSelectLoading, name, false);
          })
        } catch (e) {
          console.log(e)
        } finally {
          this.$forceUpdate();
          this.loading = false;
        }
      },
      submit() {

        this.formValidated = true;

        if (this.formIsFilled) {

          this.params.type = this.type;
          this.params.condition = this.condition;

          let params = {};
          _.mapKeys(this.params, (value, key) => {
            if (value || typeof value === 'boolean') {
              params[key] = value && value.value !== undefined ? value.value : value && value.id ? value.id : value;
            }
          });
          if (this.additionalParams[0].external || this.additionalParams[0].external.value) {
            params.params = [];
            this.additionalParams.map(param => {
              params.params.push({
                external: this.getValueOf(param.external),
                wallkit: this.getValueOf(param.wallkit)
              })
            });
          }

          //check and fill settings options
          let settings = {};
          if( !_.isEmpty(this.settings.webhook_url) ) {
            if( !this.isWebhookUrlValid ) {
              return;
            }
            settings.webhook_url = this.settings.webhook_url;
          }

          if( !_.isEmpty(settings) ) {
            params.settings = settings;
          }

          this.$emit('save', params);
        }
      },
      changeTemplates() {
        this.$set(this.paramsOptions, 'wallkit', []);
        this.$set(this.paramsOptions, 'external', []);

        if (this.type.value === 'hubspot'
            || this.type.value === 'mailchimp'
            || this.type.value === 'streak'
            || this.type.value === 'activecampaign'
            || this.type.value === 'campaignmonitor'
            || this.type.value === 'segment'){

          axios.get(`/admin/integrations/${this.type.value}/sync-fields`).then(response => {
            if (response) {

              this.$set(this.paramsOptions, 'wallkit', response.data.wallkit);

              if (this.type.value !== 'segment') {
                _.mapKeys(response.data[this.type.value], (value, key) => {
                  this.paramsOptions.external.push(
                      {
                        label: value,
                        value: key
                      }
                  );
                  this.additionalParams.map(param => {
                    if (param.external === key) {
                      param.external = {
                        label: value,
                        value: key
                      }
                    }
                  })
                });
              }
            }
          })
        }
      },
    },
    watch: {
      type() {
        this.changeTemplates()
      },
      condition(value, prevValue) {
        if (prevValue) {
          this.params.value_from_event = null
        }
      }
    }
  }
</script>
